import * as React from 'react';

import Select, { CSSObjectWithLabel, StylesConfig } from 'react-select';
import { DefaultSelectProps, ISelectOption, UskinnedSelectStyles } from '../models/select';

import { DateUtility, DayJSDateStringFormat } from '../utilities/date';
import { ObjectUtility } from '../utilities/object';
import { StringUtility } from '../utilities/string';

const DateOptions = [];
for (let date = 1; date <= 31; date++) {
    DateOptions.push({
        label: date,
        value: date
    });
}

const MonthOptions = [];
const MonthDisplayMap = {
    1: 'Jan',
    2: 'Feb',
    3: 'Mar',
    4: 'Apr',
    5: 'May',
    6: 'Jun',
    7: 'Jul',
    8: 'Aug',
    9: 'Sep',
    10: 'Oct',
    11: 'Nov',
    12: 'Dec'
};
for (let month = 1; month <= 12; month++) {
    MonthOptions.push({
        label: MonthDisplayMap[month],
        value: month
    });
}

const generateYearOptions = (start: number, end: number) => {
    const YearOptions = [];
    for (let year = start; year <= end; year++) {
        YearOptions.push({
            label: year,
            value: year
        });
    }
    return YearOptions;
};

const datePickerStyles: StylesConfig = {
    ...UskinnedSelectStyles,
    container: (provided: CSSObjectWithLabel) => ({
        ...provided,
        display: 'inline-block',
        width: 'auto',
        minWidth: 100
    })
};

export interface IMonthPickerDefaultValue {
    month: number;
    year: number;
}

export interface IDatePickerDefaultValue extends IMonthPickerDefaultValue {
    date: number;
}

interface IDatePickerCommonProps {
    onChange?: (value: string) => void;
    disabled?: boolean;
    yearRange?: {
        start: number;
        end: number;
    };
}

type IDatePickerProps =
    | ({ isMonthPicker: true; defaultValue: IMonthPickerDefaultValue } & IDatePickerCommonProps)
    | ({ isMonthPicker?: false; defaultValue: IDatePickerDefaultValue } & IDatePickerCommonProps);

interface IDatePickerComponentProps extends Omit<DefaultSelectProps, 'defaultValue'> {
    defaultValue?: number;
    options: Array<ISelectOption>;
}

const DatePickerComponent = (props: IDatePickerComponentProps) => {
    const { options, defaultValue } = props;
    return (
        <Select
            {...props}
            {...(defaultValue
                ? {
                    defaultValue: options.filter(option => option.value + '' === defaultValue + '')[0]
                }
                : {})}
            styles={datePickerStyles}
        />
    );
};

export const DatePicker = ({
    defaultValue,
    onChange,
    disabled = false,
    isMonthPicker = false,
    yearRange = null
}: IDatePickerProps) => {
    const YearOptions = React.useMemo(() => {
        const thisYear = DateUtility.getCurrentYear();
        if (!yearRange) {
            return generateYearOptions(thisYear, thisYear + 5);
        }
        return generateYearOptions(yearRange.start, yearRange.end);
    }, [yearRange]);

    // Handling default value - START
    const hasDefaultValue = React.useMemo(() => {
        let hasDefaultValue = defaultValue && defaultValue.month && defaultValue.year;

        if (hasDefaultValue && !isMonthPicker) {
            hasDefaultValue = hasDefaultValue && (defaultValue as IDatePickerDefaultValue).date;
        }
        return hasDefaultValue;
    }, [(defaultValue as IDatePickerDefaultValue)?.date, defaultValue?.month, defaultValue?.year]);

    const defaultDateStr = React.useMemo(() => {
        if (!hasDefaultValue || isMonthPicker) {
            return '';
        }
        return StringUtility.addZero((defaultValue as IDatePickerDefaultValue).date, 2);
    }, [hasDefaultValue]);
    const defaultMonthStr = React.useMemo(() => {
        if (!hasDefaultValue) {
            return '';
        }
        return StringUtility.addZero(defaultValue.month, 2);
    }, [hasDefaultValue]);
    const defaultYearStr = React.useMemo(() => {
        if (!hasDefaultValue) {
            return '';
        }
        return defaultValue.year + '';
    }, [hasDefaultValue]);

    const defaultDate = React.useMemo(() => {
        if (!hasDefaultValue) {
            return '';
        }
        let dateFormat = DayJSDateStringFormat.YYYYMM;
        if (!isMonthPicker) {
            dateFormat = DayJSDateStringFormat.YYYYMMDD;
        }
        let fullDateStr = `${defaultYearStr}-${defaultMonthStr}`;
        if (!isMonthPicker) {
            fullDateStr = `${fullDateStr}-${defaultDateStr}`;
        }

        const isValidDefaultValue = DateUtility.isValidDateStringByFormat(fullDateStr, dateFormat);
        if (!isValidDefaultValue) {
            return '';
        }
        return fullDateStr;
    }, [hasDefaultValue]);

    const getDefaultValue = React.useCallback(
        (type: 'date' | 'month' | 'year') => {
            if (!defaultDate) {
                return {};
            }
            const value = defaultValue[type];
            if (!ObjectUtility.isNil(value)) {
                return {
                    defaultValue: value
                };
            }
            return {};
        },
        [defaultDate]
    );
    // Handling default value - END

    const [fullDate, setFullDate] = React.useState(defaultDate);
    const [tempDate, setTempDate] = React.useState(defaultDateStr);
    const [tempMonth, setTempMonth] = React.useState(defaultMonthStr);
    const [tempYear, setTempYear] = React.useState(defaultYearStr);

    const isFirstRun = React.useRef(true);

    React.useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }

        // Case month picker
        if (isMonthPicker) {
            if (!tempMonth || !tempYear) {
                setFullDate('');
                return;
            }
            setFullDate(`${tempYear}-${tempMonth}`);
            return;
        }

        // Case date picker
        if (!tempDate || !tempMonth || !tempYear) {
            setFullDate('');
            return;
        }
        setFullDate(`${tempYear}-${tempMonth}-${tempDate}`);
    }, [tempDate, tempMonth, tempYear]);

    React.useEffect(() => {
        onChange(fullDate);
    }, [fullDate]);

    return (
        <>
            {/* Year - current year to the next 5 years */}
            <DatePickerComponent
                options={YearOptions}
                {...getDefaultValue('year')}
                placeholder='Year'
                className='mr-2'
                onChange={(newValue: ISelectOption) => {
                    setTempYear(StringUtility.addZero(newValue.value, 4));
                }}
                isDisabled={disabled}
            />
            {/* Month - 1 to 12 */}
            <DatePickerComponent
                options={MonthOptions}
                {...getDefaultValue('month')}
                placeholder='Month'
                className='mr-2'
                onChange={(newValue: ISelectOption) => {
                    setTempMonth(StringUtility.addZero(newValue.value, 2));
                }}
                isDisabled={disabled}
            />
            {/* Date - 1 to 31 */}
            {isMonthPicker ? (
                ''
            ) : (
                <DatePickerComponent
                    options={DateOptions}
                    {...getDefaultValue('date')}
                    placeholder='Date'
                    onChange={(newValue: ISelectOption) => {
                        setTempDate(StringUtility.addZero(newValue.value, 2));
                    }}
                    isDisabled={disabled}
                />
            )}
        </>
    );
};
